import { ReactNode, useState, forwardRef, useImperativeHandle } from 'react'
import { Tooltip, ITooltipProps } from '@wiggui/ds/Tooltip'
import { composeClasses } from '../../../utils/src/strings'

const CLASS_SWITCH_WRAPPER = 'aspect-[8/5] relative inline-flex group'
const CLASS_ROUNDED_CENTER =
  'rounded-full absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%]'
const CLASS_SWITCH_BG = `h-[70%] w-[80%] ${CLASS_ROUNDED_CENTER}`
const CLASS_SWITCH_OUTER_SLIDER = `rounded-full absolute top-[50%] translate-y-[-50%] aspect-square h-[100%] transition-all`
const CLASS_SWITCH_INNER_SIZE = 'h-[50%] w-[50%]'
const CLASS_SWITCH_INNER_SLIDER = `${CLASS_SWITCH_INNER_SIZE} ${CLASS_ROUNDED_CENTER}`

const CLASS_PIVOT_LEFT = 'left-0'
const CLASS_PIVOT_RIGHT = 'left-[37.5%]'

const WrapperTooltip = ({
  tooltipContent,
  children,
  tooltipPlacement
}: {
  tooltipContent?: string
  tooltipPlacement?: ITooltipProps['placement']
  children: ReactNode
}) => {
  if (!tooltipContent) {
    return <>{children}</>
  }

  return (
    <Tooltip content={tooltipContent} placement={tooltipPlacement}>
      {children}
    </Tooltip>
  )
}

export interface SwitchV2Props {
  id: string
  label?: string
  initialChecked: boolean
  isDisabled?: boolean
  isSmall?: boolean
  onToggle: (isChecked: boolean) => void
  customBackground?: string
  activeIcon?: ReactNode
  inactiveIcon?: ReactNode
  tooltipContent?: string
  tooltipPlacement?: ITooltipProps['placement']
}

export interface SwitchV2Ref {
  toggle: () => void
  setChecked: (checked: boolean) => void
}

export const SwitchV2 = forwardRef<SwitchV2Ref, SwitchV2Props>(
  (
    {
      id,
      label,
      initialChecked,
      isDisabled,
      isSmall,
      onToggle,
      customBackground,
      activeIcon,
      inactiveIcon,
      tooltipContent,
      tooltipPlacement
    },
    ref
  ) => {
    const [isChecked, setIsChecked] = useState(initialChecked)
    const [isBeingPressed, setIsBeingPressed] = useState(false)
    const [isBeingRelease, setIsBeingRelease] = useState(false)

    const handleChange = () => {
      if (isDisabled) {
        return
      }
      setIsChecked(!isChecked)
      onToggle(!isChecked)
    }

    const setChecked = (checked: boolean) => {
      if (isDisabled) {
        return
      }
      setIsChecked(checked)
      onToggle(checked)
    }

    // Exponer métodos al componente padre
    useImperativeHandle(
      ref,
      () => ({
        toggle: handleChange,
        setChecked
      }),
      [isChecked, isDisabled, onToggle]
    )

    return (
      <WrapperTooltip
        tooltipContent={tooltipContent}
        tooltipPlacement={tooltipPlacement}
      >
        <div className="flex items-center gap-2 select-none">
          <div
            className={composeClasses(
              CLASS_SWITCH_WRAPPER,
              isDisabled ? 'cursor-auto' : 'cursor-pointer',
              isSmall
                ? 'min-w-[2.75rem] max-w-[2.75rem]'
                : 'min-w-[3.75rem] max-w-[3.75rem]'
            )}
            onMouseDown={() => {
              setIsBeingRelease(false)
              setIsBeingPressed(true)
            }}
            onMouseUp={() => {
              setIsBeingPressed(false)
              setIsBeingRelease(true)
            }}
            onClick={handleChange}
          >
            <input
              id={id}
              type="checkbox"
              checked={isChecked}
              disabled={isDisabled}
              className="opacity-0 h-0 w-0 absolute"
              onChange={handleChange}
            />

            <span
              className={composeClasses(
                CLASS_SWITCH_BG,
                !isDisabled && isChecked && !customBackground && 'bg-primary',
                !isDisabled &&
                  !isChecked &&
                  !customBackground &&
                  'bg-surface-gray-20',
                customBackground
              )}
            />

            <span
              className={composeClasses(
                CLASS_SWITCH_OUTER_SLIDER,
                !isDisabled && 'group-hover:bg-notif-gray-01',
                isChecked ? CLASS_PIVOT_RIGHT : CLASS_PIVOT_LEFT
              )}
            >
              <span
                className={composeClasses(
                  CLASS_ROUNDED_CENTER,
                  'bg-[#d0d0d047] transition-all',
                  !isDisabled && isBeingPressed
                    ? 'h-full w-full'
                    : CLASS_SWITCH_INNER_SIZE
                )}
              />

              <span
                className={composeClasses(
                  CLASS_ROUNDED_CENTER,
                  !isDisabled && isBeingRelease && 'switch-pulse'
                )}
              />
            </span>

            <span
              className={composeClasses(
                CLASS_SWITCH_OUTER_SLIDER,
                isChecked ? CLASS_PIVOT_RIGHT : CLASS_PIVOT_LEFT
              )}
            >
              <span
                className={composeClasses(
                  CLASS_SWITCH_INNER_SLIDER,
                  isDisabled ? 'bg-secondary' : 'bg-white',
                  'flex items-center justify-center transition-all duration-300 ease-in-out'
                )}
              >
                {isChecked ? activeIcon : inactiveIcon}
              </span>
            </span>
          </div>

          {label && label?.length > 0 ? (
            <label
              htmlFor={id}
              className={composeClasses(
                'select-none',
                isSmall ? 'text-body-m' : 'text-body-l',
                isDisabled
                  ? 'cursor-auto text-secondary'
                  : 'cursor-pointer text-primary'
              )}
            >
              {label}
            </label>
          ) : null}
        </div>
      </WrapperTooltip>
    )
  }
)

export default SwitchV2
